import { getStatementRollingFeeData, getVisibleSetupFeeEntries, hasVisibleRollingReserveFees, hasVisibleStatementFees, isVisibleFeeValue, parseUtcDate } from "../statementPresentation";
import type { IStatementFees } from "../statementPresentation";
import { msg } from "../../locales/translate";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import Decimal from "decimal.js";
import { FC, ReactNode } from "react";
import { formatAmount } from "src/tools/tools";
import "./fonts";

export { getStatementRollingFeeData, getStatementSetupFeeData } from "../statementPresentation";
export type { IStatementFees, TStatementSetupFeeData, TStatementRollingFeeData } from "../statementPresentation";

interface IStatementTotals {
	setup?: number;
	monthly?: number;
	total_net?: number;
	total_fees?: number;
	withdrawal?: number;
	total_amount?: number;
	total_failed?: number;
	total_refunds?: number;
	total_chargebacks?: number;
	total_oct?: number;
	total_rollings?: number;
	total_ajustment_in?: number;
	total_ajustment_out?: number;
	total_release_rollings?: number;
}

interface IReportTotals {
	total_net?: number;
	total_fees?: number;
	count_failed?: number;
	total_amount?: number;
	total_failed?: number;
	count_refunds?: number;
	count_chargebacks?: number;
	count_oct?: number;
	count_success?: number;
	total_refunds?: number;
	total_chargebacks?: number;
	total_oct?: number;
	total_rollings?: number;
	count_ajustment_in?: number;
	total_ajustment_in?: number;
	count_ajustment_out?: number;
	total_ajustment_out?: number;
	total_release_rollings?: number;
}

interface IStatementChannelReport {
	channel_id?: number | string;
	channel_alias?: string;
	channel_name?: string;
	total?: Record<string, IReportTotals> | null;
}

interface IStatementMerchant {
	id?: number | string;
	name?: string;
	reports?: IStatementChannelReport[];
}

interface IStatementReport {
	id: number;
	date: string;
	data: {
		id?: number;
		name?: string;
		totals: Record<string, IReportTotals> | null;
		merchants?: IStatementMerchant[];
	};
}

export interface StatementPDFProps {
	statementId: number;
	clientName: string;
	clientId: number;
	registrationNumber?: string | number;
	clientStatus?: string;
	status: string;
	periodStart?: string | null;
	periodEnd?: string | null;
	calculatedAt?: string | null;
	rejectedReason?: string | null;
	rejectedBy?: string | null;
	totals?: Record<string, IStatementTotals>;
	fees?: IStatementFees;
	reports: IStatementReport[];
	isPayswix?: boolean;
}

const formatDate = (value?: string | Date | null) => {
	const date = parseUtcDate(value);
	return date
		? new Intl.DateTimeFormat(undefined, { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(date)
		: "";
};

const formatDateTime = (value?: string | Date | null) => {
	const date = parseUtcDate(value);
	return date
		? new Intl.DateTimeFormat(undefined, {
				timeZone: "UTC",
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
				hour12: false,
			}).format(date)
		: "";
};

const HeaderInfoRow: FC<{ label: string; value?: string | number | null; bold?: boolean; danger?: boolean; children?: ReactNode }> = ({ label, value, bold, danger, children }) => {

	if ((value === undefined || value === null || value === "") && !children) return null;

	return (
		<View style={s.headerInfoRow}>
			<Text style={[s.headerInfoLabel, danger ? s.headerInfoDanger : {}]}>{label}</Text>
			<View style={s.headerInfoValueWrap}>
				{children ?? <Text style={[s.headerInfoValue, bold ? s.headerInfoValueBold : {}, danger ? s.headerInfoDanger : {}]}>{String(value)}</Text>}
			</View>
		</View>
	);
};

// ── Totals table ──

const TOTALS_COLS: { label: string; key: string }[] = [
	{ get label() { return msg("ccy"); }, key: "ccy" },
	{ get label() { return msg("turnover"); }, key: "amount" },
	// { label: "ADJ IN", key: "adj_in" },
	// { label: "ADJ OUT", key: "adj_out" },
	{ get label() { return msg("refunds"); }, key: "refunds" },
	{ get label() { return msg("chargebacks"); }, key: "chargebacks" },
	{ get label() { return msg("oct"); }, key: "oct" },
	{ get label() { return msg("failed"); }, key: "failed" },
	{ get label() { return msg("rollings"); }, key: "rollings" },
	{ get label() { return msg("released"); }, key: "released" },
	{ get label() { return msg("fees"); }, key: "fees" },
	{ get label() { return msg("setup"); }, key: "setup" },
	{ get label() { return msg("monthly"); }, key: "monthly" },
	{ get label() { return msg("withdrawal"); }, key: "withdrawal" },
	{ get label() { return msg("net_e2ffa4f1"); }, key: "net" },
];

const hasNonZeroTotalsColumn = (totals: Record<string, IStatementTotals> | undefined, key: "setup" | "monthly") => {
	return Object.values(totals ?? {}).some(item => Number(item[key] ?? 0) !== 0);
};

const PAYSWIX_LOGO_PATHS = [
	"M8.177-17.245C6.735-17.245 5.395-16.324 4.363-14.854V-23.257H0V-.204H4.364V-2.46C5.426-.954 6.769 0 8.212 0H9.928C13.605 0 15.598-2.218 15.598-6.285L15.563-10.961C15.53-15.062 13.571-17.245 9.897-17.245H8.177ZM8.487-13.968C10.17-13.968 11.271-12.705 11.271-10.824L11.304-6.42C11.304-4.508 10.204-3.277 8.52-3.277H6.907C5.326-3.277 4.364-4.814 4.364-6.764V-11.131C4.536-12.737 5.464-13.968 6.872-13.968H8.487Z",
	"M33.155-17.075H28.791V-14.103C27.898-15.844 26.695-17.245 24.942-17.245H22.744C20.169-17.245 18.004-15.301 18.004-12.398 18.004-9.495 19.824-8.025 22.744-7.615L28.791-6.796V-5.702C28.791-4.132 28.001-3.381 26.762-3.381H24.911C23.57-3.381 23.02-4.473 23.02-5.531H19.035C19.035-1.809 20.96-.033 24.566-.033H27.657C31.23-.033 33.155-2.082 33.155-5.803V-17.075ZM24.841-14.067H25.425C27.381-14.067 28.791-12.36 28.791-10.348V-9.562L24.841-10.111C23.295-10.313 22.298-10.792 22.298-12.192 22.298-13.317 23.122-14.067 24.841-14.067Z",
	"M43.013-22.916H38.823L40.955-17.008 34.531-.204H38.859L43.013-11.408 47.069-.204H51.261L43.013-22.916Z",
	"M58.887-10.482C55.831-9.835 52.087-9.253 52.087-5.051 52.087-1.878 54.079-.066 57.616-.033H60.195C63.937-.066 65.93-1.946 65.93-5.735H62.014C62.014-4.198 61.19-3.381 59.506-3.381H58.372C56.964-3.381 56.378-3.79 56.378-5.086 56.378-6.382 57.82-6.658 59.608-7.035 62.805-7.717 66.272-9.014 66.272-12.225 66.272-15.436 64.107-17.177 60.089-17.245H58.405C54.213-17.177 51.948-15.266 51.948-11.541H55.831C55.831-13.078 56.759-13.899 58.028-13.899H59.987C61.189-13.899 62.014-13.217 62.014-12.294 62.082-10.994 61.156-10.961 58.886-10.482H58.887Z",
	"M71.292-.204 73.938-11.577 77.235-.204H81.219L84.517-11.577 87.162-.204H91.355L86.716-17.076H82.458L79.298-5.397 76.136-17.076H71.944L67.307-.204H71.291 71.292Z",
	"M93.075-.204H97.436V-17.075H93.075V-.204Z",
	"M100.001-.27C100.014-.249 100.02-.235 100.031-.226 100.039-.216 100.052-.203 100.064-.203H105.503C107.381-3.01 109.27-5.834 111.175-8.679 109.264-11.466 107.354-14.288 105.511-17.111H99.989C101.868-14.306 103.731-11.528 105.616-8.715",
	"M114.257-13.087C115.221-14.466 116.129-15.761 117.035-17.054 117.024-17.076 117.02-17.09 117.01-17.098 117.002-17.111 116.991-17.125 116.977-17.125H111.496C112.453-15.78 113.322-14.437 114.257-13.087Z",
	"M111.548-.218H117.037C116.104-1.553 115.183-2.87 114.253-4.203 113.341-2.857 112.448-1.541 111.548-.218Z",
];

export const PayswixLogo = ({ width, height }: { width: number; height: number }) => { return ((
	<Svg width={width} height={height} viewBox="0 0 155.04004 36.559999">
		{PAYSWIX_LOGO_PATHS.map((d, index) => (
			<Path key={index} d={d} fill="#111111" transform="matrix(1.221,0,0,-1.221,6.0930178,4.0720217)" />
		))}
	</Svg>
)); };

const PayswixFooter = () => { return ((
	<View style={s.payswixFooterContent}>
		<View style={s.footerLogo}>
			<PayswixLogo width={50} height={12} />
		</View>
		<View style={s.payswixFooterInfo}>
			<Text style={s.payswixFooterText}>UAB Payswix, Spaudos str. 7, 05132, Vilnius, Lithuania.</Text>
			<Text style={s.payswixFooterText}>Company No: 304604766; VAT No: LT100015296012 </Text>
		</View>
	</View>
)); };

const getVisibleTotalsCols = (totals: Record<string, IStatementTotals> | undefined) => {
	const showSetup = hasNonZeroTotalsColumn(totals, "setup");
	const showMonthly = hasNonZeroTotalsColumn(totals, "monthly");

	return TOTALS_COLS.filter(col => {
		if (col.key === "setup") return showSetup;
		if (col.key === "monthly") return showMonthly;
		return true;
	});
};

const TotalsRow: FC<{ currency: string; totals: IStatementTotals; cols: typeof TOTALS_COLS }> = ({ currency, totals, cols }) => {

	const values: Record<string, string> = {
		ccy: currency,
		amount: formatAmount(totals.total_amount ?? 0),
		adj_in: formatAmount(totals.total_ajustment_in ?? 0),
		adj_out: formatAmount(totals.total_ajustment_out ?? 0),
		refunds: formatAmount(totals.total_refunds ?? 0),
		chargebacks: formatAmount(totals.total_chargebacks ?? 0),
		oct: formatAmount(totals.total_oct ?? 0),
		failed: formatAmount(totals.total_failed ?? 0),
		rollings: formatAmount(totals.total_rollings ?? 0),
		released: formatAmount(totals.total_release_rollings ?? 0),
		fees: formatAmount(totals.total_fees ?? 0),
		setup: formatAmount(totals.setup ?? 0),
		monthly: formatAmount(totals.monthly ?? 0),
		withdrawal: formatAmount(totals.withdrawal ?? 0),
		net: formatAmount(totals.total_net ?? 0),
	};

	return (
		<View style={s.tableRow}>
			{cols.map((col, index) => (
				<Text
					key={col.key}
					style={[
						s.tableCell,
						index === cols.length - 1 ? s.tableCellLast : {},
						col.key === "ccy" ? s.tableCellCcy : s.tableCellNum,
						col.key === "net" ? s.tableCellBold : {},
					]}>
					{values[col.key]}
				</Text>
			))}
		</View>
	);
};

// ── Daily report rows ──

const REPORT_COLS: { label: string; key: string }[] = [
	{ get label() { return msg("date_17f9a0a5"); }, key: "date" },
	{ get label() { return msg("ccy"); }, key: "ccy" },
	{ get label() { return msg("txns"); }, key: "txns" },
	{ get label() { return msg("turnover"); }, key: "amount" },
	// { label: "ADJ IN", key: "adj_in" },
	// { label: "ADJ OUT", key: "adj_out" },
	{ get label() { return msg("refunds"); }, key: "refunds" },
	{ get label() { return msg("chargebacks"); }, key: "chargebacks" },
	{ get label() { return msg("oct"); }, key: "oct" },
	{ get label() { return msg("failed"); }, key: "failed" },
	{ get label() { return msg("rollings"); }, key: "rollings" },
	{ get label() { return msg("released"); }, key: "released" },
	{ get label() { return msg("fees"); }, key: "fees" },
	{ get label() { return msg("net_e2ffa4f1"); }, key: "net" },
];

const ReportRows: FC<{ reports: IStatementReport[] }> = ({ reports }) => {

	const sorted = [...reports].sort((a, b) => a.date.localeCompare(b.date));
	const rows: { date: string; ccy: string; values: Record<string, string>; showDate: boolean }[] = [];

	for (const r of sorted) {
		const entries = r.data.totals ? Object.entries(r.data.totals) : [];
		if (entries.length === 0) {
			rows.push({
				date: r.date,
				ccy: "—",
				showDate: true,
				values: { date: formatDate(r.date), ccy: "—", txns: "—", amount: "—", adj_in: "—", adj_out: "—", refunds: "—", chargebacks: "—", oct: "—", failed: "—", rollings: "—", released: "—", fees: "—", net: "—" },
			});
		} else {
			entries.forEach(([ccy, t], i) => {
				rows.push({
					date: r.date,
					ccy,
					showDate: i === 0,
					values: {
						date: formatDate(r.date),
						ccy,
						txns: String(t.count_success ?? 0),
						amount: formatAmount(t.total_amount ?? 0),
						adj_in: formatAmount(t.total_ajustment_in ?? 0),
						adj_out: formatAmount(t.total_ajustment_out ?? 0),
						refunds: formatAmount(t.total_refunds ?? 0),
						chargebacks: formatAmount(t.total_chargebacks ?? 0),
						oct: formatAmount(t.total_oct ?? 0),
						failed: formatAmount(t.total_failed ?? 0),
						rollings: formatAmount(t.total_rollings ?? 0),
						released: formatAmount(t.total_release_rollings ?? 0),
						fees: formatAmount(t.total_fees ?? 0),
						net: formatAmount(t.total_net ?? 0),
					},
				});
			});
		}
	}

	return (
		<>
			{rows.map((row, idx) => (
				<View key={idx} style={[s.tableRow, idx % 2 === 0 ? s.tableRowAlt : {}]}>
					{REPORT_COLS.map((col, index) => (
						<Text
							key={col.key}
							style={[
								s.tableCell,
								index === REPORT_COLS.length - 1 ? s.tableCellLast : {},
								col.key === "date" ? s.tableCellDate : col.key === "ccy" ? s.tableCellCcy : s.tableCellNum,
								col.key === "net" ? s.tableCellBold : {},
							]}>
							{col.key === "date" ? (row.showDate ? row.values.date : "") : row.values[col.key]}
						</Text>
					))}
				</View>
			))}
		</>
	);
};

// ── Merchant/channel totals ──

const CHANNEL_REPORT_COLS: { label: string; key: string }[] = [
	{ get label() { return msg("channel_597c1159"); }, key: "channel" },
	{ get label() { return msg("ccy"); }, key: "ccy" },
	{ get label() { return msg("txns"); }, key: "txns" },
	{ get label() { return msg("turnover"); }, key: "amount" },
	{ get label() { return msg("refunds"); }, key: "refunds" },
	{ get label() { return msg("chargebacks"); }, key: "chargebacks" },
	{ get label() { return msg("oct"); }, key: "oct" },
	{ get label() { return msg("failed"); }, key: "failed" },
	{ get label() { return msg("rollings"); }, key: "rollings" },
	{ get label() { return msg("released"); }, key: "released" },
	{ get label() { return msg("fees"); }, key: "fees" },
	{ get label() { return msg("net_e2ffa4f1"); }, key: "net" },
];

interface IMerchantChannelMetrics {
	txns: Decimal;
	amount: Decimal;
	refunds: Decimal;
	chargebacks: Decimal;
	oct: Decimal;
	failed: Decimal;
	rollings: Decimal;
	released: Decimal;
	fees: Decimal;
	net: Decimal;
}

interface IMerchantChannelGroup {
	key: string;
	name: string;
	channels: {
		key: string;
		name: string;
		currencies: { currency: string; metrics: IMerchantChannelMetrics }[];
	}[];
}

const emptyMerchantChannelMetrics = (): IMerchantChannelMetrics => ({
	txns: new Decimal(0),
	amount: new Decimal(0),
	refunds: new Decimal(0),
	chargebacks: new Decimal(0),
	oct: new Decimal(0),
	failed: new Decimal(0),
	rollings: new Decimal(0),
	released: new Decimal(0),
	fees: new Decimal(0),
	net: new Decimal(0),
});

const metricDecimal = (value: unknown) => {
	if (typeof value !== "number" && typeof value !== "string") return new Decimal(0);
	try {
		const decimal = new Decimal(value);
		return decimal.isFinite() ? decimal : new Decimal(0);
	} catch {
		return new Decimal(0);
	}
};

const formatDecimalAmount = (value: Decimal) => (value.decimalPlaces() < 2 ? value.toFixed(2) : value.toString());

const aggregateMerchantChannels = (reports: IStatementReport[]): IMerchantChannelGroup[] => {
	type TCurrencyAggregate = { currency: string; metrics: IMerchantChannelMetrics };
	type TChannelAggregate = { key: string; name: string; currencies: Map<string, TCurrencyAggregate> };
	type TMerchantAggregate = { key: string; name: string; channels: Map<string, TChannelAggregate> };

	const merchants = new Map<string, TMerchantAggregate>();

	for (const dailyReport of reports) {
		for (const merchant of dailyReport.data.merchants ?? []) {
			const merchantName = merchant.name?.trim() || String(merchant.id ?? msg("unknownMerchant"));
			const merchantKey = String(merchant.id ?? merchantName);
			let merchantAggregate = merchants.get(merchantKey);
			if (!merchantAggregate) {
				merchantAggregate = { key: merchantKey, name: merchantName, channels: new Map() };
				merchants.set(merchantKey, merchantAggregate);
			}

			for (const channelReport of merchant.reports ?? []) {
				if (channelReport.channel_id === 0 || channelReport.channel_id === "0") continue;

				const channelName = channelReport.channel_name?.trim() || channelReport.channel_alias?.trim() || String(channelReport.channel_id ?? msg("unknownChannel"));
				const channelKey = String(channelReport.channel_id ?? channelName);
				let channelAggregate = merchantAggregate.channels.get(channelKey);
				if (!channelAggregate) {
					channelAggregate = { key: channelKey, name: channelName, currencies: new Map() };
					merchantAggregate.channels.set(channelKey, channelAggregate);
				}

				for (const [currency, total] of Object.entries(channelReport.total ?? {})) {
					let currencyAggregate = channelAggregate.currencies.get(currency);
					if (!currencyAggregate) {
						currencyAggregate = { currency, metrics: emptyMerchantChannelMetrics() };
						channelAggregate.currencies.set(currency, currencyAggregate);
					}

					const metrics = currencyAggregate.metrics;
					metrics.txns = metrics.txns.plus(metricDecimal(total.count_success));
					metrics.amount = metrics.amount.plus(metricDecimal(total.total_amount));
					metrics.refunds = metrics.refunds.plus(metricDecimal(total.total_refunds));
					metrics.chargebacks = metrics.chargebacks.plus(metricDecimal(total.total_chargebacks));
					metrics.oct = metrics.oct.plus(metricDecimal(total.total_oct));
					metrics.failed = metrics.failed.plus(metricDecimal(total.total_failed));
					metrics.rollings = metrics.rollings.plus(metricDecimal(total.total_rollings));
					metrics.released = metrics.released.plus(metricDecimal(total.total_release_rollings));
					metrics.fees = metrics.fees.plus(metricDecimal(total.total_fees));
					metrics.net = metrics.net.plus(metricDecimal(total.total_net));
				}
			}
		}
	}

	return [...merchants.values()]
		.map(merchant => ({
			key: merchant.key,
			name: merchant.name,
			channels: [...merchant.channels.values()]
				.map(channel => ({
					key: channel.key,
					name: channel.name,
					currencies: [...channel.currencies.values()].sort((a, b) => a.currency.localeCompare(b.currency)),
				}))
				.sort((a, b) => a.name.localeCompare(b.name)),
		}))
		.filter(merchant => merchant.channels.length > 0)
		.sort((a, b) => a.name.localeCompare(b.name));
};

const MerchantChannelSections: FC<{ reports: IStatementReport[] }> = ({ reports }) => {

	const merchants = aggregateMerchantChannels(reports);
	if (merchants.length === 0) return null;

	return (
		<>
			{merchants.map(merchant => (
				<View key={merchant.key} style={s.section} wrap>
					<Text style={s.merchantTitle}>{merchant.name}</Text>
					<View style={s.table}>
						<View style={[s.tableRow, s.tableHeaderRow]}>
							{CHANNEL_REPORT_COLS.map((col, index) => (
								<Text
									key={col.key}
									style={[
										s.tableCell,
										s.tableCellTop,
										index === CHANNEL_REPORT_COLS.length - 1 ? s.tableCellLast : {},
										s.tableHeaderCell,
										col.key === "channel" ? s.tableCellChannel : col.key === "ccy" ? s.tableCellCcy : s.tableCellNum,
									]}>
									{col.label}
								</Text>
							))}
						</View>
						{merchant.channels.flatMap(channel =>
							channel.currencies.map(({ currency, metrics }, currencyIndex) => {
								const values: Record<string, string> = {
									channel: currencyIndex === 0 ? channel.name : "",
									ccy: currency,
									txns: metrics.txns.toString(),
									amount: formatDecimalAmount(metrics.amount),
									refunds: formatDecimalAmount(metrics.refunds),
									chargebacks: formatDecimalAmount(metrics.chargebacks),
									oct: formatDecimalAmount(metrics.oct),
									failed: formatDecimalAmount(metrics.failed),
									rollings: formatDecimalAmount(metrics.rollings),
									released: formatDecimalAmount(metrics.released),
									fees: formatDecimalAmount(metrics.fees),
									net: formatDecimalAmount(metrics.net),
								};

								return (
									<View key={`${channel.key}:${currency}`} style={s.tableRow} wrap={false}>
										{CHANNEL_REPORT_COLS.map((col, index) => (
											<Text
												key={col.key}
												style={[
													s.tableCell,
													index === CHANNEL_REPORT_COLS.length - 1 ? s.tableCellLast : {},
													col.key === "channel" ? s.tableCellChannel : col.key === "ccy" ? s.tableCellCcy : s.tableCellNum,
													col.key === "net" ? s.tableCellBold : {},
												]}>
												{values[col.key]}
											</Text>
										))}
									</View>
								);
							}),
						)}
					</View>
				</View>
			))}
		</>
	);
};

// ── Fees block ──

const formatSetupFeeValue = (key: string, val: string | number) => {
	if (typeof val !== "number") return String(val);
	if (key === "fx_fee") return `${val}%`;
	if (key === "t_cut_off_day") return String(val);
	return formatAmount(val);
};

const FeesSection: FC<{ fees: IStatementFees }> = ({ fees }) => {

	const rollings = fees?.rollings;
	const rollingData = getStatementRollingFeeData(fees);
	const setupEntries = getVisibleSetupFeeEntries(fees);
	const hasRollings = hasVisibleRollingReserveFees(fees);
	const rollingReserved = rollingData?.reserved;
	const rollingPeriod = rollingData?.period;
	const rollingVolume = rollingData?.volume;
	const rollingCap = rollingData?.cap;

	if (setupEntries.length === 0 && !hasRollings) return null;

	return (
		<View style={s.section}>
			<Text style={s.sectionTitle}>{msg("fees_6d80486c")}</Text>
			{setupEntries.length > 0 && (
				<View style={s.feesBlock}>
					<Text style={s.feesLabel}>{msg("setupFees")}</Text>
					<View style={s.feesRow}>
						{setupEntries.map(([key, val]) => (
							<View key={key} style={s.feesItem}>
								<Text style={s.feesItemKey}>{key.replace(/_/g, " ")}:</Text>
								<Text style={s.feesItemVal}>{formatSetupFeeValue(key, val)}</Text>
							</View>
						))}
					</View>
				</View>
			)}
			{hasRollings && rollings && (
				<View style={s.feesBlock}>
					<Text style={s.feesLabel}>{msg("rollingReserve")}</Text>
					{rollings.activated_from && <Text style={s.feesSubtext}>{msg("activeFrom")}{formatDateTime(rollings.activated_from)}</Text>}
					<View style={s.feesRow}>
						{isVisibleFeeValue(rollingReserved) && (
							<View style={s.feesItem}>
								<Text style={s.feesItemKey}>{msg("reserved")}</Text>
								<Text style={s.feesItemVal}>{rollingReserved}%</Text>
							</View>
						)}
						{isVisibleFeeValue(rollingPeriod) && (
							<View style={s.feesItem}>
								<Text style={s.feesItemKey}>{msg("period")}</Text>
								<Text style={s.feesItemVal}>{rollingPeriod}{msg("days")}</Text>
							</View>
						)}
						{isVisibleFeeValue(rollingVolume) && (
							<View style={s.feesItem}>
								<Text style={s.feesItemKey}>{msg("volume")}</Text>
								<Text style={s.feesItemVal}>{rollingVolume}</Text>
							</View>
						)}
						{isVisibleFeeValue(rollingCap) && (
							<View style={s.feesItem}>
								<Text style={s.feesItemKey}>{msg("cap")}</Text>
								<Text style={s.feesItemVal}>{rollingCap ? "yes" : "no"}</Text>
							</View>
						)}
					</View>
				</View>
			)}
		</View>
	);
};

// ── Main document ──

export const StatementPDF: FC<StatementPDFProps> = (props) => {

	const {
		statementId, clientName, clientId, registrationNumber,
		periodStart, periodEnd, calculatedAt,
		rejectedReason, rejectedBy,
		totals, fees, reports, isPayswix,
	} = props;

	const period = periodStart && periodEnd ? `${formatDate(periodStart)} – ${formatDate(periodEnd)}` : null;
	const totalsCols = getVisibleTotalsCols(totals);

	return (
		<Document>
			<Page size="A4" orientation="portrait" style={s.page}>
				{/* Header */}
				<View style={s.docHeader} fixed>
					{isPayswix && (
						<View style={s.headerLogo}>
							<PayswixLogo width={148} height={30} />
						</View>
					)}
					{!isPayswix && <Text style={s.docDate}>{msg("generated")}{formatDate(new Date())}</Text>}
				</View>

				{/* Client info */}
				<Text style={s.docTitle}>{msg("statement")}</Text>
				<View style={s.clientBlock}>
					<View style={s.headerSummaryRow}>
						<View style={s.headerInfoContent}>
							<HeaderInfoRow label={msg("name")}>
								<Text style={s.clientName}>{clientName}</Text>
							</HeaderInfoRow>
							<HeaderInfoRow label={msg("clientId_8726db01")} value={clientId} />
							<HeaderInfoRow label={msg("registrationNumber")} value={registrationNumber || "-"} />
							<HeaderInfoRow label={msg("statementId")} value={statementId} />
							<HeaderInfoRow label={msg("period_6e795d4d")} value={period} />
							<HeaderInfoRow label={msg("calculated")} value={formatDateTime(calculatedAt)} />
							<HeaderInfoRow label={msg("rejected")} value={rejectedReason} danger />
							<HeaderInfoRow label={msg("rejectedBy")} value={rejectedBy} />
						</View>
					</View>
				</View>

				{/* Totals */}
				{totals && Object.keys(totals).length > 0 && (
					<View style={s.section}>
						<Text style={s.sectionTitle}>{msg("totals")}</Text>
						<View style={s.table}>
							<View style={[s.tableRow, s.tableHeaderRow]}>
								{totalsCols.map((col, index) => (
									<Text key={col.key} style={[s.tableCell, s.tableCellTop, index === totalsCols.length - 1 ? s.tableCellLast : {}, s.tableHeaderCell, col.key === "ccy" ? s.tableCellCcy : s.tableCellNum]}>
										{col.label}
									</Text>
								))}
							</View>
							{Object.entries(totals).map(([ccy, t]) => (
								<TotalsRow key={ccy} currency={ccy} totals={t} cols={totalsCols} />
							))}
						</View>
					</View>
				)}

				{/* Fees */}
				{hasVisibleStatementFees(fees) && fees && <FeesSection fees={fees} />}

				{/* Merchant/channel totals */}
				<MerchantChannelSections reports={reports} />

				{/* Daily reports */}
				{reports.length > 0 && (
					<View style={s.section} wrap>
						<Text style={s.sectionTitle}>{msg("dailyReports")}{reports.length})</Text>
						<View style={s.table}>
							<View style={[s.tableRow, s.tableHeaderRow]} fixed>
								{REPORT_COLS.map((col, index) => (
									<Text key={col.key} style={[s.tableCell, s.tableCellTop, index === REPORT_COLS.length - 1 ? s.tableCellLast : {}, s.tableHeaderCell, col.key === "date" ? s.tableCellDate : col.key === "ccy" ? s.tableCellCcy : s.tableCellNum]}>
										{col.label}
									</Text>
								))}
							</View>
							<ReportRows reports={reports} />
						</View>
					</View>
				)}

				{/* Footer */}
				<View style={s.footer} fixed>
					{isPayswix ? <PayswixFooter /> : <Text style={s.footerText}>{msg("generated")}{formatDate(new Date())}</Text>}
					<Text style={s.footerText} render={({ pageNumber, totalPages }) => msg("templatePage_1bcd3a91", { v0: pageNumber, v1: totalPages })} />
				</View>
			</Page>
		</Document>
	);
};

// ── Styles ──

const s = StyleSheet.create({
	page: {
		paddingTop: 24,
		paddingHorizontal: 42,
		paddingBottom: 62,
		fontFamily: "Euclid",
		fontSize: 8,
		color: "#111111",
	},
	docHeader: {
		flexDirection: "row",
		justifyContent: "flex-end",
		alignItems: "flex-start",
		minHeight: 54,
		marginBottom: 12,
	},
	docTitle: { fontSize: 15, fontWeight: 400, color: "#111111", marginBottom: 12 },
	docDate: { fontSize: 8, color: "#555555", marginTop: 2 },
	headerLogo: { width: 148, height: 30 },

	// Client
	clientBlock: {
		marginBottom: 28,
	},
	headerSummaryRow: {
		flexDirection: "row",
		alignItems: "flex-start",
		justifyContent: "flex-start",
	},
	headerInfoContent: {
		flexDirection: "column",
		width: "100%",
	},
	headerInfoColumnLeft: {
		width: 225,
	},
	headerInfoColumnRight: {
		width: 235,
	},
	headerInfoDivider: {
		width: 1,
		backgroundColor: "#d4d4d4",
		marginLeft: 18,
		marginRight: 18,
	},
	headerInfoRow: {
		flexDirection: "row",
		alignItems: "center",
		minHeight: 11,
		marginBottom: 1,
	},
	headerInfoLabel: {
		fontSize: 9,
		fontWeight: 700,
		color: "#111111",
		marginRight: 3,
	},
	headerInfoValueWrap: {
		flexDirection: "row",
	},
	headerInfoValue: {
		fontSize: 9,
		color: "#111111",
	},
	headerInfoValueBold: {
		fontWeight: 700,
	},
	headerInfoDanger: {
		color: "#111111",
	},
	clientName: {
		fontSize: 9,
		fontWeight: 400,
		color: "#111111",
	},

	// Sections
	section: { marginBottom: 12 },
	sectionTitle: { fontSize: 9, fontWeight: 700, marginBottom: 6 },

	// Table
	table: { flexDirection: "column" },
	tableRow: { flexDirection: "row" },
	tableRowAlt: { backgroundColor: "#ffffff" },
	tableHeaderRow: { backgroundColor: "#f2f2f2" },
	tableCell: {
		flex: 1,
		padding: "6pt 5pt",
		fontSize: 5.8,
		borderBottom: "0.5pt solid #d9d9d9",
		borderLeft: "0.5pt solid #d9d9d9",
	},
	tableCellTop: {
		borderTop: "0.5pt solid #d9d9d9",
	},
	tableCellLast: {
		borderRight: "0.5pt solid #d9d9d9",
	},
	tableHeaderCell: {
		fontSize: 5.8,
		fontWeight: 700,
		color: "#111111",
		textTransform: "uppercase",
	},
	tableCellCcy: { flex: 0, minWidth: 24, maxWidth: 32 },
	tableCellDate: { flex: 0, minWidth: 46, maxWidth: 50 },
	tableCellChannel: {
		flexGrow: 0,
		flexShrink: 0,
		flexBasis: 140,
		width: 140,
		minWidth: 140,
		maxWidth: 140,
		textAlign: "left",
	},
	tableCellNum: { textAlign: "right" },
	tableCellBold: { fontWeight: 700 },
	merchantTitle: { fontSize: 9, fontWeight: 700, marginBottom: 6 },

	// Fees
	feesBlock: {
		paddingBottom: 5,
		marginBottom: 5,
		borderBottom: "0.5pt solid #d9d9d9",
	},
	feesLabel: {
		fontSize: 7.4,
		fontWeight: 700,
		color: "#111111",
		marginBottom: 4,
	},
	feesSubtext: { fontSize: 7, color: "#555555", marginBottom: 3 },
	feesRow: { flexDirection: "row", flexWrap: "wrap", gap: 3 },
	feesItem: { flexDirection: "row", gap: 3, alignItems: "flex-end", minWidth: 118, marginBottom: 1 },
	feesItemKey: { fontSize: 7, color: "#555555" },
	feesItemVal: { fontSize: 7.4, fontFamily: "Euclid", color: "#111111", fontWeight: 500 },

	// Footer
	footer: {
		position: "absolute",
		bottom: 18,
		left: 42,
		right: 42,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-end",
		borderTop: "0.6pt solid #111111",
		paddingTop: 7,
	},
	footerText: { fontSize: 7, color: "#111111" },
	payswixFooterContent: {
		flexDirection: "row",
		alignItems: "center",
		width: 420,
	},
	footerLogo: { width: 50, height: 12, marginRight: 34 },
	payswixFooterInfo: {
		flexDirection: "column",
	},
	payswixFooterCompany: {
		fontSize: 7,
		fontWeight: 700,
		color: "#111111",
		marginBottom: 1,
	},
	payswixFooterText: { fontSize: 4.8, color: "#111111" },
});
